import {
  Resolver,
  ResolverError,
  ResolverSuccess,
  appendErrors,
  transformToNestObject,
} from "react-hook-form";
import { ParseParams, z } from "zod";

// Formerly we were using zodResolver in @hookform/resolvers, which is a pretty old version (1.3.3) that doesn't support asyncParse
// I tried to upgrade it but it requires >=2.0.0 to support asyncParse, which is a dependent to react-hook-form@7.0.0, which also needs a upgrade
// So I give it up in case that may lead to more dependency upgrades and potential compatibility risks
// This file is copied from @hookform/resolvers@1.3.3, just switched to asyncParse

const convertArrayToPathName = (paths: (string | number)[]): string =>
  paths
    .reduce(
      (previous, path: string | number, index): string =>
        `${previous}${typeof path === "string" ? `${index > 0 ? "." : ""}${path}` : `[${path}]`}`,
      ""
    )
    .toString();

const parseErrorSchema = (zodError: z.ZodError, validateAllFieldCriteria: boolean) => {
  if (zodError.isEmpty) {
    return {};
  }

  const errors = [...zodError.errors];
  let previous: Record<string, any> = {};

  for (const error of errors) {
    const { path, message, code: type } = error;
    const currentPath = convertArrayToPathName(path);

    if ("unionErrors" in error) {
      for (const subErrors of error.unionErrors.map((e) => e.errors)) {
        errors.push(...subErrors);
      }
    }

    previous = {
      ...previous,
      ...(path
        ? previous[currentPath] && validateAllFieldCriteria
          ? {
              [currentPath]: appendErrors(currentPath, validateAllFieldCriteria, previous, type, message),
            }
          : {
              [currentPath]: previous[currentPath] || {
                message,
                type,
                ...(validateAllFieldCriteria
                  ? {
                      types: { [type]: message || true },
                    }
                  : {}),
              },
            }
        : {}),
    };
  }

  return previous;
};

const asyncZodResolver =
  <T extends z.ZodSchema<any, any>>(schema: T, options?: ParseParams): Resolver<z.infer<T>> =>
  async (values, _, validateAllFieldCriteria = false) => {
    const result = await schema.safeParseAsync(values, options);

    if (result.success) {
      return { values: result.data, errors: {} } as ResolverSuccess<z.infer<T>>;
    }

    return {
      values: {},
      errors: transformToNestObject(parseErrorSchema(result.error, validateAllFieldCriteria)),
    } as ResolverError<z.infer<T>>;
  };

export default asyncZodResolver;
